/**
 * GCS Manager Admin Module
 * Handles file operations for Google Cloud Storage management interface
 */
(function () {
  "use strict";

  const GCSManager = {
    // Configuration
    config: {
      apiBaseUrl: "/admin/gcs-manager",
      maxFileSize: 8 * 1024 * 1024 * 1024, // 8GB
      chunkSize: 5 * 1024 * 1024, // 5MB chunks for large uploads
      allowedFileTypes: {
        image: ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"],
        video: ["mp4", "avi", "mov", "mkv", "webm", "flv", "wmv", "m4v"],
        audio: ["mp3", "wav", "flac", "aac", "ogg", "m4a", "wma"],
        document: [
          "pdf",
          "doc",
          "docx",
          "xls",
          "xlsx",
          "ppt",
          "pptx",
          "txt",
          "csv",
          "rtf",
        ],
        archive: ["zip", "rar", "7z", "tar", "gz", "bz2"],
        code: [
          "html",
          "css",
          "js",
          "json",
          "xml",
          "php",
          "py",
          "java",
          "cpp",
          "c",
          "h",
        ],
        subtitle: ["srt", "vtt", "sub", "ass", "ssa"],
      },
    },

    // State management
    state: {
      currentPath: "",
      selectedFiles: new Set(),
      viewMode: "list", // Default to table/list view
      sortBy: "name",
      sortOrder: "asc",
      searchQuery: "",
      filterType: "",
      filterDateFrom: "",
      filterDateTo: "",
      filterSizeMin: "",
      filterSizeMax: "",
      uploadQueue: [],
      currentUploads: new Map(),
      pageToken: null,
      hasMore: false,
      isLoading: false,
    },

    // Initialize the module
    init() {
      this.csrfToken =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content") ||
        document.querySelector("#csrf-token")?.value;

      // Remove modal-hidey class from all modals
      document.querySelectorAll(".modal-hidey").forEach((modal) => {
        modal.classList.remove("modal-hidey");
      });

      this.bindElements();
      this.bindEvents();
      this.initializeFileUpload();

      // Read hash from URL on load
      const hashPath = this.getPathFromHash();
      if (hashPath !== null) {
        this.state.currentPath = hashPath;
      }

      this.loadFiles(this.state.currentPath);
      this.restoreViewMode();

      // Note: Modals are auto-initialized by $(document).foundation()
      // called at the bottom of the page, so we don't manually initialize them here

      // Fix Foundation modal positioning to use our CSS centering
      this.fixModalPositioning();
    },

    // Remove Foundation's inline positioning to allow CSS centering to work
    fixModalPositioning() {
      const modals = document.querySelectorAll(".reveal[data-reveal]");
      modals.forEach((modal) => {
        // Listen for Foundation's open event
        $(modal).on("open.zf.reveal", function () {
          // Remove inline positioning that Foundation adds
          this.style.removeProperty("top");
          this.style.removeProperty("left");
        });
      });
    },

    // Bind DOM elements
    bindElements() {
      this.elements = {
        folderTree: document.getElementById("folder-tree"),
        fileList: document.getElementById("files-list-body"),
        fileGrid: document.getElementById("files-grid"),
        breadcrumb: document.getElementById("breadcrumb"),
        searchInput: document.getElementById("search-files"),
        filterTypeSelect: document.getElementById("filter-type"),
        filterDateFrom: document.getElementById("filter-date-from"),
        filterDateTo: document.getElementById("filter-date-to"),
        filterSizeMin: document.getElementById("filter-size-min"),
        filterSizeMax: document.getElementById("filter-size-max"),
        applyFiltersBtn: document.getElementById("apply-filters"),
        clearFiltersBtn: document.getElementById("clear-filters"),
        uploadBtn: document.getElementById("upload-btn"),
        newFolderBtn: document.getElementById("new-folder-btn"),
        bulkDeleteBtn: document.getElementById("bulk-delete-btn"),
        bulkDownloadBtn: document.getElementById("bulk-download-btn"),
        selectAllFilesBtn: document.getElementById("select-all-files"),
        fileActions: document.getElementById("file-actions"),
        sortBySelect: document.getElementById("sort-by"),
        uploadModal: document.getElementById("upload-modal"),
        folderModal: document.getElementById("folder-modal"),
        previewModal: document.getElementById("preview-modal"),
        deleteModal: document.getElementById("delete-modal"),
        moveModal: document.getElementById("move-modal"),
        uploadArea: document.getElementById("gcs-upload-area"), // FileUpload component
        loadingIndicator: document.getElementById("loading-indicator"),
        emptyState: document.getElementById("empty-state"),
        errorContainer: document.getElementById("error-container"),
        storageInfo: document.getElementById("storage-info"),
      };
    },

    // Bind event listeners
    bindEvents() {
      // Preview modal footer: act on the file currently being previewed
      document
        .getElementById("preview-download")
        ?.addEventListener("click", () => {
          if (this.previewPath) this.downloadFile(this.previewPath);
        });
      document
        .getElementById("preview-share")
        ?.addEventListener("click", () => {
          if (this.previewPath) this.sharePreviewLink(this.previewPath);
        });

      // View mode toggle buttons
      document
        .getElementById("view-mode-table")
        ?.addEventListener("click", () => {
          this.setViewMode("list");
        });
      document
        .getElementById("view-mode-grid")
        ?.addEventListener("click", () => {
          this.setViewMode("grid");
        });

      // Search and filters
      this.elements.searchInput?.addEventListener(
        "input",
        this.debounce(() => this.handleSearch(), 300)
      );
      this.elements.applyFiltersBtn?.addEventListener("click", () =>
        this.applyFilters()
      );
      this.elements.clearFiltersBtn?.addEventListener("click", () =>
        this.clearFilters()
      );

      // File operations
      this.elements.uploadBtn?.addEventListener("click", () =>
        this.showUploadModal()
      );
      this.elements.newFolderBtn?.addEventListener("click", () =>
        this.showFolderModal()
      );
      this.elements.bulkDeleteBtn?.addEventListener("click", () =>
        this.deleteSelected()
      );
      this.elements.bulkDownloadBtn?.addEventListener("click", () =>
        this.downloadSelected()
      );
      this.elements.selectAllFilesBtn?.addEventListener("change", (e) =>
        this.toggleSelectAll(e.target.checked)
      );

      // Sort control
      this.elements.sortBySelect?.addEventListener("change", () =>
        this.handleSort()
      );

      // Upload modal
      this.elements.uploadFileInput?.addEventListener("change", (e) =>
        this.handleFileSelect(e)
      );
      document
        .getElementById("upload-submit")
        ?.addEventListener("click", () => this.processUploadQueue());

      // Folder modal - use the correct ID from HTML
      document
        .getElementById("create-folder-submit")
        ?.addEventListener("click", () => this.createFolder());

      // Modal close buttons
      document.querySelectorAll(".modal .close-button").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          const modal = e.target.closest(".modal");
          this.closeModal(modal);
        });
      });

      // Keyboard shortcuts
      document.addEventListener("keydown", (e) => this.handleKeyboard(e));

      // Window resize
      window.addEventListener(
        "resize",
        this.debounce(() => this.handleResize(), 250)
      );

      // Close dropdowns when clicking outside
      document.addEventListener("click", (e) => {
        if (!e.target.closest(".button-dropdown")) {
          document.querySelectorAll(".button-dropdown.open").forEach((d) => {
            d.classList.remove("open");
          });
        }
      });

      // Listen for hash changes (browser back/forward)
      window.addEventListener("hashchange", () => {
        const hashPath = this.getPathFromHash();
        if (hashPath !== null && hashPath !== this.state.currentPath) {
          this.state.currentPath = hashPath;
          this.state.selectedFiles.clear();
          this.loadFiles(hashPath);
        }
      });
    },

    // Initialize FileUpload component integration
    initializeFileUpload() {
      const uploadArea = this.elements.uploadArea;
      if (!uploadArea) return;

      // Set current path as data attribute for FileUpload to use
      uploadArea.setAttribute("data-upload-path", this.state.currentPath || "");
      console.log(
        "Set upload path data attribute:",
        this.state.currentPath || "root"
      );

      // Check if FileUpload component is already initialized
      if (!uploadArea.fileUpload && typeof FileUpload !== "undefined") {
        // Manually initialize the FileUpload component
        uploadArea.fileUpload = new FileUpload(uploadArea);
        console.log(
          "FileUpload component manually initialized for GCS Manager"
        );

        // Override the upload method to add CSRF token and path
        const originalUpload = uploadArea.fileUpload.upload.bind(
          uploadArea.fileUpload
        );
        uploadArea.fileUpload.upload = async function (url, onProgress) {
          // Store original XMLHttpRequest.prototype.send
          const originalSend = XMLHttpRequest.prototype.send;
          const csrfToken = document.querySelector("#csrf-token")?.value;

          // Override XMLHttpRequest.prototype.send temporarily
          const currentPath = GCSManager.state.currentPath;
          XMLHttpRequest.prototype.send = function (data) {
            if (data instanceof FormData) {
              if (csrfToken) {
                data.append("csrf_token", csrfToken);
                console.log("Added CSRF token to upload request");
              }
              // Add current path to FormData
              data.append("path", currentPath || "");
              console.log(
                "Added current path to upload request:",
                currentPath || "root"
              );
            }
            return originalSend.call(this, data);
          };

          try {
            // Call original upload method with url parameter
            const result = await originalUpload(url, onProgress);
            // Restore original send method
            XMLHttpRequest.prototype.send = originalSend;
            return result;
          } catch (error) {
            // Restore original send method even on error
            XMLHttpRequest.prototype.send = originalSend;
            throw error;
          }
        };

        // Also override uploadSingleFile for batch uploads
        if (uploadArea.fileUpload.uploadSingleFile) {
          const originalUploadSingle = uploadArea.fileUpload.uploadSingleFile.bind(
            uploadArea.fileUpload
          );
          uploadArea.fileUpload.uploadSingleFile = async function (
            file,
            index
          ) {
            // For fetch-based uploads, we need to intercept differently
            const originalFetch = window.fetch;
            const csrfToken = document.querySelector("#csrf-token")?.value;

            const currentPath = GCSManager.state.currentPath;
            window.fetch = function (url, options) {
              if (options && options.body instanceof FormData) {
                if (csrfToken) {
                  options.body.append("csrf_token", csrfToken);
                  console.log("Added CSRF token to batch upload request");
                }
                // Add current path to FormData
                options.body.append("path", currentPath || "");
                console.log(
                  "Added current path to batch upload request:",
                  currentPath || "root"
                );
              }
              return originalFetch.call(this, url, options);
            };

            try {
              const result = await originalUploadSingle(file, index);
              window.fetch = originalFetch;
              return result;
            } catch (error) {
              window.fetch = originalFetch;
              throw error;
            }
          };
        }
      }

      // Listen for file selection events from the FileUpload component
      uploadArea.addEventListener("fileselect", (e) => {
        const files = e.detail.files;
        console.log("Files selected:", files);
        // The FileUpload component handles the upload automatically
        // We don't need to manually process files here
      });

      // Listen for successful upload events (single file uploads)
      uploadArea.addEventListener("fileuploaded", (e) => {
        console.log("File uploaded successfully:", e.detail);

        // For single file uploads, reload and close modal
        if (e.detail.file) {
          this.loadFiles(this.state.currentPath);
          this.showSuccess(
            `File "${e.detail.file.name}" uploaded successfully`
          );
          this.closeModal(this.elements.uploadModal);
        }
      });

      // Listen for batch upload completion events
      uploadArea.addEventListener("batchuploaded", (e) => {
        console.log("Batch upload completed:", e.detail);

        // Reload the file list when batch upload completes
        this.loadFiles(this.state.currentPath);

        // Show success message with stats
        const { total, success, errors, cancelled } = e.detail;
        if (errors === 0 && cancelled === 0) {
          this.showSuccess(`All ${total} file(s) uploaded successfully`);
        } else if (success > 0) {
          this.showSuccess(
            `${success} of ${total} file(s) uploaded successfully`
          );
        }

        // Close the modal after batch upload completes
        this.closeModal(this.elements.uploadModal);
      });

      // Listen for upload error events
      uploadArea.addEventListener("fileuploaderror", (e) => {
        console.error("Upload error:", e.detail);
        this.showError(`Failed to upload file: ${e.detail.error}`);
      });

      // Listen for file removal events (if user removes a file from the queue)
      uploadArea.addEventListener("fileremove", (e) => {
        // Handle file removal from queue if needed
        console.log("File removed from queue:", e.detail);
      });
    },

    // Load files from server
    async loadFiles(path = "", append = false) {
      if (this.state.isLoading) return;

      this.state.isLoading = true;
      this.showLoading(true);

      // Update the upload path data attribute whenever we load files
      if (this.elements.uploadArea) {
        this.elements.uploadArea.setAttribute(
          "data-upload-path",
          path || this.state.currentPath || ""
        );
        console.log(
          "Updated upload path data attribute:",
          path || this.state.currentPath || "root"
        );
      }

      try {
        const params = new URLSearchParams({
          path: path || this.state.currentPath,
          search: this.state.searchQuery,
          type: this.state.filterType,
          date_from: this.state.filterDateFrom,
          date_to: this.state.filterDateTo,
          size_min: this.state.filterSizeMin,
          size_max: this.state.filterSizeMax,
          limit: 100,
        });

        if (append && this.state.pageToken) {
          params.append("page_token", this.state.pageToken);
        }

        const response = await fetch(
          `${this.config.apiBaseUrl}/list?${params}`
        );
        const data = await response.json();

        console.log("GCS Response:", data); // Debug log

        if (data.error) {
          throw new Error(data.error);
        }

        this.state.currentPath = path || this.state.currentPath;
        this.state.pageToken = data.nextPageToken;
        this.state.hasMore = !!data.nextPageToken;
        this.state.searchActive = !!data.searchActive;
        this.state.searchTruncated = !!data.truncated;
        this.state.searchMatchCap = data.matchCap || 0;

        if (append) {
          this.appendFiles(data.files);
        } else {
          this.renderFiles(data.files || []);
          // Use allFolders for the complete tree, or folders for current level
          const foldersData = data.allFolders || data.folders || [];
          console.log("Folders data received:", foldersData);
          this.renderFolderTree(foldersData);
        }

        this.updateBreadcrumb();
        this.updateStorageInfo(data.storageInfo);
        this.updateSelectionControls();
      } catch (error) {
        this.showError("Failed to load files: " + error.message);
      } finally {
        this.state.isLoading = false;
        this.showLoading(false);
      }
    },

    // Render files in table or grid view
    renderFiles(files) {
      const listContainer = this.elements.fileList;
      const gridContainer = this.elements.fileGrid;

      if (!files || files.length === 0) {
        this.showEmptyState(true);
        if (listContainer) listContainer.innerHTML = "";
        if (gridContainer) gridContainer.innerHTML = "";
        return;
      }

      this.showEmptyState(false);
      this.renderSearchBanner(files.length);

      // Render based on current view mode
      if (this.state.viewMode === "grid") {
        // Render grid view
        if (gridContainer) {
          gridContainer.innerHTML = "";
          files.forEach((file) => {
            const element = this.createFileGridItem(file);
            gridContainer.appendChild(element);
          });
          gridContainer.style.display = "grid";
        }

        // Hide list view
        const listWrapper = document.getElementById("files-list");
        if (listWrapper) {
          listWrapper.style.display = "none";
        }
      } else {
        // Render list view
        if (listContainer) {
          listContainer.innerHTML = "";
          files.forEach((file) => {
            const element = this.createFileListItem(file);
            listContainer.appendChild(element);
          });
        }

        // Show list view
        const listWrapper = document.getElementById("files-list");
        if (listWrapper) {
          listWrapper.style.display = "block";
        }

        // Hide grid view
        if (gridContainer) {
          gridContainer.style.display = "none";
        }
      }

      // Bind file item events
      this.bindFileItemEvents();
    },

    // Create grid item element
    createFileGridItem(file) {
      const div = document.createElement("div");
      div.className = "file-item";
      div.dataset.path = file.path || file.name;
      div.dataset.type = file.type;

      const icon = this.getFileIcon(file);
      const size = this.formatFileSize(file.size || 0);
      const filePath = (file.path || file.name).replace(/'/g, "\\'");
      const filePathEscaped = filePath.replace(/"/g, "&quot;");
      const isVideo = this.isVideoFile(file);
      const fullPath = file.path || file.name;
      const parentDir = fullPath.includes("/")
        ? fullPath.substring(0, fullPath.lastIndexOf("/"))
        : "";
      const titleAttr = this.escapeHtml(fullPath);
      const showFolder = this.state.searchActive && parentDir;
      const folderHtml = showFolder
        ? `<div class="file-folder" style="font-size: 0.75rem; color: #888; margin-top: 2px; word-break: break-all;" title="${this.escapeHtml(
            parentDir
          )}"><i class="fas fa-folder" style="margin-right: 4px;"></i>${this.escapeHtml(
            parentDir
          )}</div>`
        : "";

      div.innerHTML = `
                <div class="file-checkbox">
                    <input type="checkbox" data-path="${
                      file.path || file.name
                    }">
                </div>
                <div class="file-icon">
                    <i class="${icon}"></i>
                </div>
                <div class="file-name" title="${titleAttr}">
                    ${file.name}
                    ${folderHtml}
                </div>
                <div class="file-size">
                    ${size}
                </div>
                <div class="file-actions">
                    <div class="button-dropdown align-right">
                        <button class="button small icon-only secondary">
                            <i class="fas fa-ellipsis-v"></i>
                        </button>
                        <div class="dropdown-menu">
                            <button class="dropdown-item preview-btn" data-path="${filePathEscaped}"><i class="fas fa-eye"></i> Preview</button>
                            <button class="dropdown-item download-btn" data-path="${filePathEscaped}"><i class="fas fa-download"></i> Download</button>
                            <button class="dropdown-item copy-gcs-btn" data-path="${filePathEscaped}"><i class="fas fa-link"></i> Copy GCS URL</button>
                            ${
                              isVideo
                                ? `<button class="dropdown-item analyze-btn" data-path="${filePathEscaped}"><i class="fas fa-brain"></i> Analyze with Vertex</button>`
                                : ""
                            }
                            <button class="dropdown-item alert delete-btn" data-path="${filePathEscaped}"><i class="fas fa-trash"></i> Delete</button>
                        </div>
                    </div>
                </div>
            `;

      // Setup dropdown toggle
      const dropdown = div.querySelector(".button-dropdown");
      const dropdownBtn = dropdown?.querySelector(".button");

      if (dropdownBtn && dropdown) {
        dropdownBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          // Close all other dropdowns
          document.querySelectorAll(".button-dropdown.open").forEach((d) => {
            if (d !== dropdown) d.classList.remove("open");
          });
          // Toggle this dropdown
          dropdown.classList.toggle("open");
        });
      }

      // Bind menu item events
      const previewBtn = div.querySelector(".preview-btn");
      const downloadBtn = div.querySelector(".download-btn");
      const copyGcsBtn = div.querySelector(".copy-gcs-btn");
      const analyzeBtn = div.querySelector(".analyze-btn");
      const deleteBtn = div.querySelector(".delete-btn");

      if (previewBtn) {
        previewBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.previewFile(filePath);
          dropdown?.classList.remove("open");
        });
      }
      if (downloadBtn) {
        downloadBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.downloadFile(filePath);
          dropdown?.classList.remove("open");
        });
      }
      if (copyGcsBtn) {
        copyGcsBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.copyGcsUrl(filePath);
          dropdown?.classList.remove("open");
        });
      }
      if (analyzeBtn) {
        analyzeBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.openVertexAnalysis(filePath);
          dropdown?.classList.remove("open");
        });
      }
      if (deleteBtn) {
        deleteBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.deleteFile(filePath);
          dropdown?.classList.remove("open");
        });
      }

      return div;
    },

    // Create list item element
    createFileListItem(file) {
      const tr = document.createElement("tr");
      tr.dataset.path = file.path || file.name;
      tr.dataset.type = file.type || "file";

      const icon = this.getFileIcon(file);
      const size = this.formatFileSize(file.size || 0);
      const date = this.formatDate(
        file.updated_at || file.created_at || file.updated || file.timeCreated
      );
      const filePath = (file.path || file.name).replace(/'/g, "\\'");

      // Check if it's a video file for conditional button rendering
      const isVideo = this.isVideoFile(file);

      const fullPath = file.path || file.name;
      const parentDir = fullPath.includes("/")
        ? fullPath.substring(0, fullPath.lastIndexOf("/"))
        : "";
      const showFolder = this.state.searchActive && parentDir;
      const folderHtml = showFolder
        ? `<div class="file-folder" style="font-size: 0.75rem; color: #888; margin-top: 2px;" title="${this.escapeHtml(
            parentDir
          )}"><i class="fas fa-folder" style="margin-right: 4px;"></i>${this.escapeHtml(
            parentDir
          )}</div>`
        : "";

      tr.innerHTML = `
                <td>
                    <input type="checkbox" data-path="${
                      file.path || file.name
                    }">
                </td>
                <td>
                    <i class="${icon}"></i>
                    <span class="file-name" title="${this.escapeHtml(fullPath)}">${file.name}</span>
                    ${folderHtml}
                </td>
                <td>${size}</td>
                <td>${file.contentType || file.type || "unknown"}</td>
                <td>${date}</td>
                <td>
                    <div class="button-dropdown align-right">
                        <button class="button small icon-only secondary">
                            <i class="fas fa-ellipsis-v"></i>
                        </button>
                        <div class="dropdown-menu">
                            <button class="dropdown-item preview-btn" data-path="${filePath}"><i class="fas fa-eye"></i> Preview</button>
                            <button class="dropdown-item download-btn" data-path="${filePath}"><i class="fas fa-download"></i> Download</button>
                            <button class="dropdown-item copy-gcs-btn" data-path="${filePath}"><i class="fas fa-link"></i> Copy GCS URL</button>
                            ${
                              isVideo
                                ? `<button class="dropdown-item analyze-btn" data-path="${filePath}"><i class="fas fa-brain"></i> Analyze with Vertex</button>`
                                : ""
                            }
                            <button class="dropdown-item alert delete-btn" data-path="${filePath}"><i class="fas fa-trash"></i> Delete</button>
                        </div>
                    </div>
                </td>
            `;

      // Setup dropdown toggle
      const dropdown = tr.querySelector(".button-dropdown");
      const dropdownBtn = dropdown?.querySelector(".button");

      if (dropdownBtn && dropdown) {
        dropdownBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          // Close all other dropdowns
          document.querySelectorAll(".button-dropdown.open").forEach((d) => {
            if (d !== dropdown) d.classList.remove("open");
          });
          // Toggle this dropdown
          dropdown.classList.toggle("open");
        });
      }

      // Bind menu item events
      const previewBtn = tr.querySelector(".preview-btn");
      const downloadBtn = tr.querySelector(".download-btn");
      const copyGcsBtn = tr.querySelector(".copy-gcs-btn");
      const analyzeBtn = tr.querySelector(".analyze-btn");
      const deleteBtn = tr.querySelector(".delete-btn");

      if (previewBtn) {
        previewBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.previewFile(filePath);
          dropdown?.classList.remove("open");
        });
      }
      if (downloadBtn) {
        downloadBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.downloadFile(filePath);
          dropdown?.classList.remove("open");
        });
      }
      if (copyGcsBtn) {
        copyGcsBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.copyGcsUrl(filePath);
          dropdown?.classList.remove("open");
        });
      }
      if (analyzeBtn) {
        analyzeBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.openVertexAnalysis(filePath);
          dropdown?.classList.remove("open");
        });
      }
      if (deleteBtn) {
        deleteBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.deleteFile(filePath);
          dropdown?.classList.remove("open");
        });
      }

      return tr;
    },

    // Render folder tree
    renderFolderTree(folders) {
      if (!this.elements.folderTree) return;

      const tree = this.buildFolderTree(folders);
      this.elements.folderTree.innerHTML = this.renderFolderTreeHTML(tree);

      // Bind folder click events
      this.elements.folderTree
        .querySelectorAll(".folder-item")
        .forEach((item) => {
          item.addEventListener("click", (e) => {
            e.preventDefault();
            const path = item.dataset.path;
            this.navigateToFolder(path);
          });
        });
    },

    // Build hierarchical folder structure
    buildFolderTree(folders) {
      const tree = { name: "Root", path: "", children: [] };

      folders.forEach((folder) => {
        // Handle both string and object formats
        const folderPath = typeof folder === "string" ? folder : folder.path;
        const parts = folderPath.split("/").filter((p) => p);
        let current = tree;
        let currentPath = "";

        parts.forEach((part) => {
          currentPath = currentPath ? `${currentPath}/${part}` : part;

          let child = current.children.find((c) => c.name === part);
          if (!child) {
            child = { name: part, path: currentPath, children: [] };
            current.children.push(child);
          }
          current = child;
        });
      });

      return tree;
    },

    // Render folder tree HTML
    renderFolderTreeHTML(node, level = 0) {
      let html = "";

      if (level === 0) {
        html += `
                    <div class="folder-item ${
                      this.state.currentPath === "" ? "active" : ""
                    }"
                         data-path="">
                        <i class="fas fa-home"></i> Root
                    </div>
                `;
      }

      node.children.forEach((child) => {
        const isActive = this.state.currentPath === child.path;
        const hasChildren = child.children.length > 0;

        html += `
                    <div class="folder-item nested-${level + 1} ${
          isActive ? "active" : ""
        }"
                         data-path="${child.path}">
                        <i class="fas fa-${
                          hasChildren ? "folder-open" : "folder"
                        }"></i>
                        ${child.name}
                    </div>
                    ${
                      hasChildren
                        ? this.renderFolderTreeHTML(child, level + 1)
                        : ""
                    }
                `;
      });

      return html;
    },

    // Navigate to folder
    navigateToFolder(path) {
      this.state.currentPath = path;
      this.state.selectedFiles.clear();
      // Update hash in URL
      this.setPathToHash(path);
      this.loadFiles(path);
    },

    // Get folder path from URL hash
    getPathFromHash() {
      const hash = window.location.hash;
      if (!hash || hash === "#") {
        return ""; // Root path
      }
      // Remove the '#' and decode
      return decodeURIComponent(hash.substring(1));
    },

    // Set folder path to URL hash
    setPathToHash(path) {
      const newHash = path ? `#${encodeURIComponent(path)}` : "#";
      // Only update if different to avoid triggering hashchange unnecessarily
      if (window.location.hash !== newHash) {
        window.location.hash = newHash;
      }
    },

    // Update breadcrumb navigation
    updateBreadcrumb() {
      if (!this.elements.breadcrumb) return;

      const parts = this.state.currentPath.split("/").filter((p) => p);
      let html = '<li><a href="#" data-path="">Root</a></li>';
      let currentPath = "";

      parts.forEach((part, index) => {
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        html += `<li><a href="#" data-path="${currentPath}">${part}</a></li>`;
      });

      // Find the UL within the breadcrumb nav and update it
      const breadcrumbList = this.elements.breadcrumb.querySelector(
        ".breadcrumbs"
      );
      if (breadcrumbList) {
        breadcrumbList.innerHTML = html;
      }

      // Bind breadcrumb clicks
      this.elements.breadcrumb.querySelectorAll("a").forEach((link) => {
        link.addEventListener("click", (e) => {
          e.preventDefault();
          this.navigateToFolder(link.dataset.path);
        });
      });
    },

    // Handle file selection for upload
    handleFileSelect(e) {
      const files = Array.from(e.target.files);
      this.addFilesToQueue(files);
    },

    // Add files to upload queue
    addFilesToQueue(files) {
      const validFiles = files.filter((file) => this.validateFile(file));

      validFiles.forEach((file) => {
        const id = `upload-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`;
        const queueItem = {
          id,
          file,
          progress: 0,
          status: "pending",
          error: null,
        };

        this.state.uploadQueue.push(queueItem);
        this.renderUploadQueueItem(queueItem);
      });

      this.updateUploadButton();
    },

    // Validate file before upload
    validateFile(file) {
      // Check size
      if (file.size > this.config.maxFileSize) {
        this.showError(`File "${file.name}" exceeds maximum size of 5GB`);
        return false;
      }

      // File type validation removed - all file types are now allowed

      return true;
    },

    // Render upload queue item
    renderUploadQueueItem(item) {
      const container = this.elements.uploadProgressContainer;
      if (!container) return;

      const div = document.createElement("div");
      div.className = "upload-item";
      div.id = item.id;

      div.innerHTML = `
                <div class="upload-item-info">
                    <span class="upload-item-name">${item.file.name}</span>
                    <span class="upload-item-size">${this.formatFileSize(
                      item.file.size
                    )}</span>
                </div>
                <div class="upload-item-progress">
                    <div class="progress">
                        <div class="progress-meter" style="width: 0%"></div>
                    </div>
                    <span class="upload-item-status">Pending</span>
                </div>
                <button class="upload-item-cancel" onclick="GCSManager.cancelUpload('${
                  item.id
                }')">
                    <i class="fas fa-times"></i>
                </button>
            `;

      container.appendChild(div);
    },

    // Process upload queue
    async processUploadQueue() {
      const pendingUploads = this.state.uploadQueue.filter(
        (item) => item.status === "pending"
      );

      for (const item of pendingUploads) {
        await this.uploadFile(item);
      }

      // Reload files after all uploads complete
      this.loadFiles(this.state.currentPath);
    },

    // Upload single file
    async uploadFile(item) {
      const formData = new FormData();
      formData.append("file", item.file);
      formData.append("path", this.state.currentPath);
      formData.append("csrf_token", this.csrfToken);

      // Create XMLHttpRequest for progress tracking
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Progress handler
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            this.updateUploadProgress(item.id, progress);
          }
        });

        // Complete handler
        xhr.addEventListener("load", () => {
          if (xhr.status === 200) {
            const response = JSON.parse(xhr.responseText);
            if (response.error) {
              this.updateUploadStatus(item.id, "error", response.error);
              reject(response.error);
            } else {
              this.updateUploadStatus(item.id, "complete");
              resolve(response);
            }
          } else {
            this.updateUploadStatus(item.id, "error", "Upload failed");
            reject("Upload failed");
          }
        });

        // Error handler
        xhr.addEventListener("error", () => {
          this.updateUploadStatus(item.id, "error", "Network error");
          reject("Network error");
        });

        // Abort handler
        xhr.addEventListener("abort", () => {
          this.updateUploadStatus(item.id, "cancelled");
          resolve();
        });

        // Store XHR for cancellation
        this.state.currentUploads.set(item.id, xhr);

        // Start upload
        this.updateUploadStatus(item.id, "uploading");
        xhr.open("POST", `${this.config.apiBaseUrl}/upload`);
        xhr.send(formData);
      });
    },

    // Update upload progress
    updateUploadProgress(id, progress) {
      const element = document.getElementById(id);
      if (!element) return;

      const progressBar = element.querySelector(".progress-meter");
      const statusText = element.querySelector(".upload-item-status");

      progressBar.style.width = `${progress}%`;
      statusText.textContent = `${progress}%`;
    },

    // Update upload status
    updateUploadStatus(id, status, error = null) {
      const element = document.getElementById(id);
      if (!element) return;

      const statusText = element.querySelector(".upload-item-status");
      const item = this.state.uploadQueue.find((i) => i.id === id);

      if (item) {
        item.status = status;
        item.error = error;
      }

      switch (status) {
        case "uploading":
          statusText.textContent = "Uploading...";
          element.classList.add("uploading");
          break;
        case "complete":
          statusText.textContent = "Complete";
          element.classList.add("complete");
          setTimeout(() => element.remove(), 3000);
          break;
        case "error":
          statusText.textContent = error || "Error";
          element.classList.add("error");
          break;
        case "cancelled":
          statusText.textContent = "Cancelled";
          element.remove();
          break;
      }
    },

    // Cancel upload
    cancelUpload(id) {
      const xhr = this.state.currentUploads.get(id);
      if (xhr) {
        xhr.abort();
        this.state.currentUploads.delete(id);
      }

      const index = this.state.uploadQueue.findIndex((item) => item.id === id);
      if (index > -1) {
        this.state.uploadQueue.splice(index, 1);
      }

      document.getElementById(id)?.remove();
    },

    // Create new folder
    async createFolder() {
      const nameInput = document.getElementById("folder-name");
      const name = nameInput?.value.trim();

      if (!name) {
        this.showError("Please enter a folder name");
        return;
      }

      try {
        const response = await fetch(
          `${this.config.apiBaseUrl}/create-folder`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              path: this.state.currentPath,
              name: name,
              csrf_token: this.csrfToken,
            }),
          }
        );

        const data = await response.json();

        if (data.error) {
          throw new Error(data.error);
        }

        this.closeModal(this.elements.folderModal);
        nameInput.value = "";
        this.loadFiles(this.state.currentPath);
        this.showSuccess(`Folder "${name}" created successfully`);
      } catch (error) {
        this.showError("Failed to create folder: " + error.message);
      }
    },

    // Download file using signed URL
    async downloadFile(path) {
      try {
        // Get signed URL for download
        const response = await fetch(`${this.config.apiBaseUrl}/signed-url`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            path: path,
            expiration: 3600, // 1 hour
            csrf_token: this.csrfToken,
          }),
        });

        const data = await response.json();

        if (data.error) {
          throw new Error(data.error);
        }

        // Open signed URL in new window
        window.open(data.url, "_blank");
      } catch (error) {
        this.showError("Failed to download file: " + error.message);
      }
    },

    // Preview file
    async previewFile(path) {
      // Get file metadata first
      try {
        const response = await fetch(
          `${this.config.apiBaseUrl}/metadata?path=${encodeURIComponent(path)}`
        );
        const data = await response.json();

        if (data.error) {
          throw new Error(data.error);
        }

        // Check if file can be previewed
        const extension = path.split(".").pop().toLowerCase();
        const imageTypes = this.config.allowedFileTypes.image;
        const videoTypes = this.config.allowedFileTypes.video;
        const audioTypes = this.config.allowedFileTypes.audio;

        // /metadata answers { success, metadata: {...} }; keep the flat shape as a fallback
        const meta = data.metadata || data;
        this.previewPath = path;

        const previewContent = document.getElementById("preview-content");
        const previewInfo = document.getElementById("preview-info");

        if (imageTypes.includes(extension)) {
          // Generate signed URL for image
          const signedUrl = await this.getSignedUrl(path, 3600);
          previewContent.innerHTML = `<img src="${signedUrl}" alt="${meta.name}">`;
        } else if (videoTypes.includes(extension)) {
          // Generate signed URL for video
          const signedUrl = await this.getSignedUrl(path, 3600);
          previewContent.innerHTML = `
                        <video controls>
                            <source src="${signedUrl}" type="video/${extension}">
                            Your browser does not support video playback.
                        </video>
                    `;
        } else if (audioTypes.includes(extension)) {
          // Generate signed URL for audio
          const signedUrl = await this.getSignedUrl(path, 3600);
          previewContent.innerHTML = `
                        <audio controls>
                            <source src="${signedUrl}" type="audio/${extension}">
                            Your browser does not support audio playback.
                        </audio>
                    `;
        } else {
          previewContent.innerHTML = `
                        <div class="callout secondary">
                            <p>Preview not available for this file type.</p>
                            <button class="button primary" onclick="GCSManager.downloadFile('${path}')">
                                <i class="fas fa-download"></i> Download File
                            </button>
                        </div>
                    `;
        }

        // Show file info
        previewInfo.innerHTML = `
                    <dl>
                        <dt>Name:</dt>
                        <dd>${meta.name}</dd>
                        <dt>Size:</dt>
                        <dd>${this.formatFileSize(Number(meta.size))}</dd>
                        <dt>Type:</dt>
                        <dd>${meta.content_type || meta.contentType || ""}</dd>
                        <dt>Modified:</dt>
                        <dd>${this.formatDate(meta.updated_at || meta.updated)}</dd>
                    </dl>
                `;

        this.showModal(this.elements.previewModal);
      } catch (error) {
        this.showError("Failed to preview file: " + error.message);
      }
    },

    // Share: copy a 24h signed link for the previewed file to the clipboard
    async sharePreviewLink(path) {
      try {
        const url = await this.getSignedUrl(path, 86400);
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
          this.showSuccess("Share link copied (valid for 24 hours)");
        } else {
          window.prompt("Share link (valid for 24 hours):", url);
        }
      } catch (error) {
        this.showError("Failed to create share link: " + error.message);
      }
    },

    // Get signed URL
    async getSignedUrl(path, expiration = 3600) {
      const response = await fetch(`${this.config.apiBaseUrl}/signed-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          path: path,
          expiration: expiration,
          csrf_token: this.csrfToken,
        }),
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      return data.url;
    },

    // Delete file
    async deleteFile(path) {
      const confirmText = document.getElementById("delete-message");
      const confirmBtn = document.getElementById("confirm-delete");

      if (!confirmText || !confirmBtn) {
        console.error("Delete modal elements not found");
        return;
      }

      confirmText.textContent = `Are you sure you want to delete "${path
        .split("/")
        .pop()}"?`;

      confirmBtn.onclick = async () => {
        try {
          const response = await fetch(`${this.config.apiBaseUrl}/delete`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              path: path,
              csrf_token: this.csrfToken,
            }),
          });

          const data = await response.json();

          if (data.error) {
            throw new Error(data.error);
          }

          this.closeModal(this.elements.deleteModal);
          this.loadFiles(this.state.currentPath);
          this.showSuccess("File deleted successfully");
        } catch (error) {
          this.showError("Failed to delete file: " + error.message);
        }
      };

      this.showModal(this.elements.deleteModal);
    },

    // Delete selected files
    async deleteSelected() {
      if (this.state.selectedFiles.size === 0) {
        this.showError("No files selected");
        return;
      }

      const paths = Array.from(this.state.selectedFiles);
      const confirmText = document.getElementById("delete-message");
      const confirmBtn = document.getElementById("confirm-delete");

      if (!confirmText || !confirmBtn) {
        console.error("Delete modal elements not found");
        return;
      }

      confirmText.textContent = `Are you sure you want to delete ${paths.length} file(s)?`;

      confirmBtn.onclick = async () => {
        try {
          const response = await fetch(
            `${this.config.apiBaseUrl}/bulk-delete`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                paths: paths,
                csrf_token: this.csrfToken,
              }),
            }
          );

          const data = await response.json();

          if (data.error) {
            throw new Error(data.error);
          }

          this.closeModal(this.elements.deleteModal);
          this.state.selectedFiles.clear();
          this.loadFiles(this.state.currentPath);
          this.showSuccess(`${data.deleted} file(s) deleted successfully`);
        } catch (error) {
          this.showError("Failed to delete files: " + error.message);
        }
      };

      this.showModal(this.elements.deleteModal);
    },

    // Download selected files
    async downloadSelected() {
      if (this.state.selectedFiles.size === 0) {
        this.showError("No files selected");
        return;
      }

      const paths = Array.from(this.state.selectedFiles);

      if (paths.length === 1) {
        // Single file - direct download
        this.downloadFile(paths[0]);
      } else {
        // Multiple files - create ZIP
        try {
          const form = document.createElement("form");
          form.method = "POST";
          form.action = `${this.config.apiBaseUrl}/bulk-download`;

          const pathsInput = document.createElement("input");
          pathsInput.type = "hidden";
          pathsInput.name = "paths";
          pathsInput.value = JSON.stringify(paths);

          const csrfInput = document.createElement("input");
          csrfInput.type = "hidden";
          csrfInput.name = "csrf_token";
          csrfInput.value = this.csrfToken;

          form.appendChild(pathsInput);
          form.appendChild(csrfInput);
          document.body.appendChild(form);
          form.submit();
          document.body.removeChild(form);
        } catch (error) {
          this.showError("Failed to download files: " + error.message);
        }
      }
    },

    // Show move modal
    showMoveModal() {
      if (this.state.selectedFiles.size === 0) {
        this.showError("No files selected");
        return;
      }

      // TODO: Implement folder selector in move modal
      this.showModal(this.elements.moveModal);
    },

    // Handle search
    handleSearch() {
      this.state.searchQuery = this.elements.searchInput.value;
      this.loadFiles(this.state.currentPath);
    },

    // Apply filters
    applyFilters() {
      this.state.filterType = this.elements.filterTypeSelect.value;
      this.state.filterDateFrom = this.elements.filterDateFrom.value;
      this.state.filterDateTo = this.elements.filterDateTo.value;

      // Convert MB to bytes for size filters
      const minMB = parseFloat(this.elements.filterSizeMin.value) || 0;
      const maxMB = parseFloat(this.elements.filterSizeMax.value) || 0;
      this.state.filterSizeMin = minMB > 0 ? minMB * 1024 * 1024 : "";
      this.state.filterSizeMax = maxMB > 0 ? maxMB * 1024 * 1024 : "";

      // Hide filter panel after applying
      const panel = document.getElementById("filter-card");
      if (panel) panel.style.display = "none";

      this.loadFiles(this.state.currentPath);
    },

    // Clear filters
    clearFilters() {
      this.state.filterType = "";
      this.state.filterDateFrom = "";
      this.state.filterDateTo = "";
      this.state.filterSizeMin = "";
      this.state.filterSizeMax = "";

      this.elements.filterTypeSelect.value = "";
      this.elements.filterDateFrom.value = "";
      this.elements.filterDateTo.value = "";
      this.elements.filterSizeMin.value = "";
      this.elements.filterSizeMax.value = "";

      this.loadFiles(this.state.currentPath);
    },

    // Set view mode
    setViewMode(mode) {
      this.state.viewMode = mode;
      localStorage.setItem("gcs-view-mode", this.state.viewMode);

      // Update button states
      const tableBtn = document.getElementById("view-mode-table");
      const gridBtn = document.getElementById("view-mode-grid");

      if (tableBtn && gridBtn) {
        if (mode === "list") {
          tableBtn.classList.remove("secondary");
          tableBtn.classList.add("primary");
          gridBtn.classList.remove("primary");
          gridBtn.classList.add("secondary");
        } else {
          gridBtn.classList.remove("secondary");
          gridBtn.classList.add("primary");
          tableBtn.classList.remove("primary");
          tableBtn.classList.add("secondary");
        }
      }

      // Update UI
      const gridContainer = document.getElementById("files-grid");
      const listContainer = document.getElementById("files-list");

      if (gridContainer)
        gridContainer.style.display = mode === "grid" ? "block" : "none";
      if (listContainer)
        listContainer.style.display = mode === "list" ? "block" : "none";

      // Re-render files
      this.loadFiles(this.state.currentPath);
    },

    // Toggle view mode (deprecated, use setViewMode)
    toggleViewMode() {
      const newMode = this.state.viewMode === "grid" ? "list" : "grid";
      this.setViewMode(newMode);
    },

    // Restore view mode from localStorage
    restoreViewMode() {
      const savedMode = localStorage.getItem("gcs-view-mode");
      if (savedMode && (savedMode === "grid" || savedMode === "list")) {
        this.setViewMode(savedMode);
      } else {
        this.setViewMode("list"); // Default to list view
      }
    },

    // Handle sorting
    handleSort() {
      this.state.sortBy = this.elements.sortBySelect.value;
      // TODO: Implement client-side sorting or reload with sort parameter
      this.loadFiles(this.state.currentPath);
    },

    // Toggle select all files
    toggleSelectAll(checked) {
      const checkboxes = document.querySelectorAll(
        '.file-item input[type="checkbox"][data-path], tr input[type="checkbox"][data-path]'
      );

      checkboxes.forEach((checkbox) => {
        checkbox.checked = checked;
        if (checked) {
          this.state.selectedFiles.add(checkbox.dataset.path);
        } else {
          this.state.selectedFiles.delete(checkbox.dataset.path);
        }
      });

      // Update select-all checkbox
      if (this.elements.selectAllFilesBtn) {
        this.elements.selectAllFilesBtn.checked = checked;
      }

      this.updateSelectionControls();
    },

    // Clear all selections
    clearSelection() {
      this.state.selectedFiles.clear();

      // Uncheck all checkboxes
      document
        .querySelectorAll(
          '.file-item input[type="checkbox"][data-path], tr input[type="checkbox"][data-path]'
        )
        .forEach((checkbox) => {
          checkbox.checked = false;
        });

      // Uncheck select-all checkbox
      if (this.elements.selectAllFilesBtn) {
        this.elements.selectAllFilesBtn.checked = false;
      }

      this.updateSelectionControls();
    },

    // Bind file item events
    bindFileItemEvents() {
      // Checkbox selection - handle both grid and list views
      document
        .querySelectorAll(
          '.file-item input[type="checkbox"][data-path], tr input[type="checkbox"][data-path]'
        )
        .forEach((checkbox) => {
          checkbox.addEventListener("change", (e) => {
            e.stopPropagation(); // Prevent triggering parent events
            const path = e.target.dataset.path;

            if (e.target.checked) {
              this.state.selectedFiles.add(path);
              // Add visual feedback
              const fileItem = e.target.closest(".file-item, tr");
              if (fileItem) fileItem.classList.add("selected");
            } else {
              this.state.selectedFiles.delete(path);
              // Remove visual feedback
              const fileItem = e.target.closest(".file-item, tr");
              if (fileItem) fileItem.classList.remove("selected");
            }

            // Update select-all checkbox state
            const totalCheckboxes = document.querySelectorAll(
              '.file-item input[type="checkbox"][data-path], tr input[type="checkbox"][data-path]'
            ).length;
            const allSelected =
              this.state.selectedFiles.size === totalCheckboxes;

            if (this.elements.selectAllFilesBtn) {
              this.elements.selectAllFilesBtn.checked = allSelected;
            }

            this.updateSelectionControls();
          });
        });

      // Row click to toggle checkbox (list view)
      document.querySelectorAll("#files-list-body tr").forEach((row) => {
        row.addEventListener("click", (e) => {
          // Don't toggle if clicking on checkbox, button, or dropdown
          if (
            e.target.closest('input[type="checkbox"]') ||
            e.target.closest("button") ||
            e.target.closest(".button-dropdown") ||
            e.target.closest(".dropdown-menu")
          ) {
            return;
          }

          // Find and toggle the checkbox
          const checkbox = row.querySelector(
            'input[type="checkbox"][data-path]'
          );
          if (checkbox) {
            checkbox.checked = !checkbox.checked;
            // Trigger the change event to update selection state
            checkbox.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      });

      // Double-click to open folders (grid view)
      document
        .querySelectorAll('.file-item[data-type="folder"]')
        .forEach((item) => {
          item.addEventListener("dblclick", () => {
            const path = item.dataset.path;
            this.navigateToFolder(path);
          });
        });

      // Double-click to open folders (list view)
      document.querySelectorAll('tr[data-type="folder"]').forEach((item) => {
        item.addEventListener("dblclick", () => {
          const path = item.dataset.path;
          this.navigateToFolder(path);
        });
      });
    },

    // Update selection controls
    updateSelectionControls() {
      const count = this.state.selectedFiles.size;
      const hasSelection = count > 0;

      // Enable/disable bulk action buttons
      if (this.elements.bulkDeleteBtn) {
        this.elements.bulkDeleteBtn.disabled = !hasSelection;
      }
      if (this.elements.bulkDownloadBtn) {
        this.elements.bulkDownloadBtn.disabled = !hasSelection;
      }
    },

    // Update storage info
    updateStorageInfo(info) {
      if (!this.elements.storageInfo || !info) return;

      this.elements.storageInfo.innerHTML = `
                <span>Files: ${info.fileCount}</span>
                <span>Folders: ${info.folderCount}</span>
                <span>Total Size: ${this.formatFileSize(info.totalSize)}</span>
            `;
    },

    // Handle keyboard shortcuts
    handleKeyboard(e) {
      // Only handle shortcuts when we're in the GCS manager context
      const gcsContainer = document.querySelector(".gcs-manager-container");
      const isInGcsManager = gcsContainer && gcsContainer.contains(e.target);

      // Ctrl/Cmd + A: Select all (only in GCS manager)
      if ((e.ctrlKey || e.metaKey) && e.key === "a" && isInGcsManager) {
        e.preventDefault();
        this.toggleSelectAll(true);
      }

      // Delete key: Delete selected
      if (e.key === "Delete" && this.state.selectedFiles.size > 0) {
        e.preventDefault();
        this.deleteSelected();
      }

      // Escape: Close modals
      if (e.key === "Escape") {
        // Close all open Foundation reveal modals
        document
          .querySelectorAll('.reveal.is-open, .reveal[aria-hidden="false"]')
          .forEach((modal) => {
            this.closeModal(modal);
          });
      }
    },

    // Handle window resize
    handleResize() {
      // Adjust layout for mobile
      if (window.innerWidth < 640) {
        // Hide folder tree on mobile
        if (this.elements.folderTree) {
          this.elements.folderTree.parentElement.style.display = "none";
        }
      } else {
        // Show folder tree on desktop
        if (this.elements.folderTree) {
          this.elements.folderTree.parentElement.style.display = "block";
        }
      }
    },

    // Utility functions
    showModal(modal) {
      if (modal) {
        const $modal = $(modal);
        // Modal should already be initialized by $(document).foundation()
        // Just open it using Foundation's method
        if ($modal.foundation) {
          $modal.foundation("open");
        } else {
          // Fallback if Foundation is not loaded
          modal.classList.add("is-open");
          modal.style.display = "block";
        }
      }
    },

    closeModal(modal) {
      if (modal) {
        // Use Foundation's Reveal modal system
        if ($(modal).foundation) {
          $(modal).foundation("close");
        } else {
          // Fallback for non-Foundation modals
          modal.classList.remove("is-open");
          modal.style.display = "none";
        }
      }
    },

    showUploadModal() {
      // Simply show the modal - FileUpload component handles the queue
      this.showModal(this.elements.uploadModal);
    },

    showFolderModal() {
      document.getElementById("folder-name").value = "";
      this.showModal(this.elements.folderModal);
    },

    updateUploadButton() {
      const submitBtn = document.getElementById("upload-submit");
      if (submitBtn) {
        submitBtn.disabled = this.state.uploadQueue.length === 0;
        submitBtn.textContent = `Upload ${this.state.uploadQueue.length} File(s)`;
      }
    },

    showLoading(show) {
      if (this.elements.loadingIndicator) {
        this.elements.loadingIndicator.style.display = show ? "block" : "none";
      }
    },

    showEmptyState(show) {
      if (this.elements.emptyState) {
        this.elements.emptyState.style.display = show ? "block" : "none";
      }
      if (show) {
        this.renderSearchBanner(0);
      }
    },

    escapeHtml(value) {
      if (value === null || value === undefined) return "";
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },

    renderSearchBanner(matchCount) {
      const bannerId = "gcs-search-banner";
      let banner = document.getElementById(bannerId);

      if (!this.state.searchActive) {
        if (banner) banner.remove();
        return;
      }

      const scope = this.state.currentPath
        ? `under "${this.escapeHtml(this.state.currentPath)}"`
        : "across the entire bucket";
      const cap = this.state.searchMatchCap || 500;
      const truncated = this.state.searchTruncated;
      const message = truncated
        ? `Showing first ${cap} matches ${scope}. Refine your search to narrow results.`
        : `Found ${matchCount} match${matchCount === 1 ? "" : "es"} ${scope}.`;
      const calloutClass = truncated ? "warning" : "primary";

      if (!banner) {
        banner = document.createElement("div");
        banner.id = bannerId;
        banner.style.marginBottom = "0.75rem";
        const anchor =
          document.getElementById("files-list") ||
          this.elements.fileGrid ||
          this.elements.fileList;
        if (anchor && anchor.parentNode) {
          anchor.parentNode.insertBefore(banner, anchor);
        }
      }

      banner.innerHTML = `<div class="callout ${calloutClass}" style="margin: 0; padding: 0.5rem 0.75rem;"><i class="fas fa-search" style="margin-right: 6px;"></i>${message}</div>`;
    },

    showError(message) {
      if (this.elements.errorContainer) {
        this.elements.errorContainer.innerHTML = `
                    <div class="callout alert" data-closable>
                        ${message}
                        <button class="close-button" aria-label="Close alert" type="button" data-close>
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                `;

        // Auto-dismiss after 5 seconds
        setTimeout(() => {
          this.elements.errorContainer.innerHTML = "";
        }, 5000);
      }
    },

    showSuccess(message) {
      if (this.elements.errorContainer) {
        this.elements.errorContainer.innerHTML = `
                    <div class="callout success" data-closable>
                        ${message}
                        <button class="close-button" aria-label="Close alert" type="button" data-close>
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                `;

        // Auto-dismiss after 3 seconds
        setTimeout(() => {
          this.elements.errorContainer.innerHTML = "";
        }, 3000);
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return "N/A";
        }

        // Format as YYYY-MM-DD HH:MM:SS
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch (e) {
        return "N/A";
      }
    },

    getFileIcon(file) {
      if (file.type === "folder") {
        return "fas fa-folder";
      }

      const extension = file.name.split(".").pop().toLowerCase();

      if (this.config.allowedFileTypes.image.includes(extension)) {
        return "fas fa-image";
      } else if (this.config.allowedFileTypes.video.includes(extension)) {
        return "fas fa-video";
      } else if (this.config.allowedFileTypes.audio.includes(extension)) {
        return "fas fa-music";
      } else if (this.config.allowedFileTypes.document.includes(extension)) {
        return "fas fa-file-alt";
      } else if (this.config.allowedFileTypes.archive.includes(extension)) {
        return "fas fa-file-archive";
      } else if (this.config.allowedFileTypes.code.includes(extension)) {
        return "fas fa-file-code";
      } else {
        return "fas fa-file";
      }
    },

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    // Check if file is a video file
    isVideoFile(file) {
      const extension = (file.name || file.path || "")
        .split(".")
        .pop()
        .toLowerCase();
      return this.config.allowedFileTypes.video.includes(extension);
    },

    // Copy GCS URL to clipboard
    async copyGcsUrl(path) {
      // Get bucket name from the server data or environment
      const bucketName = window.GCS_BUCKET_NAME || "studiodome-files"; // Replace with actual bucket name
      const gcsUrl = `gs://${bucketName}/${path}`;

      try {
        if (navigator.clipboard && window.isSecureContext) {
          // Modern way with Clipboard API
          await navigator.clipboard.writeText(gcsUrl);
          this.showSuccess(`GCS URL copied: ${gcsUrl}`);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement("textarea");
          textArea.value = gcsUrl;
          textArea.style.position = "fixed";
          textArea.style.left = "-999999px";
          textArea.style.top = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();

          try {
            const successful = document.execCommand("copy");
            if (successful) {
              this.showSuccess(`GCS URL copied: ${gcsUrl}`);
            } else {
              this.showError("Failed to copy URL to clipboard");
            }
          } catch (err) {
            this.showError("Failed to copy URL: " + err.message);
          } finally {
            document.body.removeChild(textArea);
          }
        }
      } catch (err) {
        this.showError("Failed to copy URL: " + err.message);
      }
    },

    // Open Vertex Analysis page with pre-filled video URL
    openVertexAnalysis(path) {
      // Get bucket name from the server data or environment
      const bucketName = window.GCS_BUCKET_NAME || "studiodome-files";
      const gcsUrl = `gs://${bucketName}/${path}`;
      const vertexUrl = `/admin/vertex-analyses/create?file_url=${encodeURIComponent(
        gcsUrl
      )}`;

      // Open in new tab
      window.open(vertexUrl, "_blank");
      this.showSuccess("Opening Vertex Analysis page...");
    },

    // Copy Vertex Analysis link to clipboard (for grid view compatibility)
    async copyVertexAnalysisLink(path) {
      // Redirect to the openVertexAnalysis function for consistency
      this.openVertexAnalysis(path);
    },
  };

  // Initialize when DOM is ready - only if GCS manager elements exist
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      // Check if any of the core GCS manager elements exist
      const hasGcsElements =
        document.getElementById("folder-tree") ||
        document.getElementById("files-list-body") ||
        document.getElementById("files-grid") ||
        document.getElementById("gcs-upload-area") ||
        document.querySelector(".gcs-manager-container");

      if (hasGcsElements) {
        GCSManager.init();
      }
    });
  } else {
    // Check if any of the core GCS manager elements exist
    const hasGcsElements =
      document.getElementById("folder-tree") ||
      document.getElementById("files-list-body") ||
      document.getElementById("files-grid") ||
      document.getElementById("gcs-upload-area") ||
      document.querySelector(".gcs-manager-container");

    if (hasGcsElements) {
      GCSManager.init();
    }
  }

  // Export to global scope
  window.GCSManager = GCSManager;
})();
